import { GrossMarginRequestDetail, UpdateCultivoDto } from "@/services/gross-margin-requests.service";
import { FieldValues, UseFormGetValues } from "react-hook-form";

// ──────────────────────── parser ────────────────────────

/**
 * Convierte valores de inputs (currency string "U$S 1.234,56" o decimal con coma "45,5")
 * a número. Exportado para que el componente padre pueda usarlo al construir el DTO.
 */
export const parseFormValue = (v: any): number => {
  if (v === "" || v === undefined || v === null) return 0;
  const str = String(v)
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  return parseFloat(str) || 0;
};

// ──────────────────────── cálculo ────────────────────────

/**
 * Calcula los indicadores del margen bruto para un cultivo dado.
 *
 * Fórmulas clave:
 *  - costoImplantacion (qq/ha) = costoImplantacionUsd / (precioCereal / cotizacionDolar)
 *  - inversionTotal (U$S/ha)   = costoImplantacionUsd + precioAlquiler
 *  - ganancia (qq/ha)          = rindeEsperado - inversionTotal(qq)
 *  - rentabilidad (%)          = (ganancia / rindeEsperado) × 100
 *
 * Los displays muestran "Esperando datos" hasta que rindeEsperado > 0.
 */
export const calcGrossMarginCultivo = (params: {
  precioCereal: number;
  cotizacionDolar: number;
  rindeEsperado: number;
  precioAgroquimicos: number;
  precioFertilizantes: number;
  precioSemillas: number;
  precioLabores: number;
  precioSeguros: number;
  precioAlquiler: number;
  gastoDeCosecha: number;
}) => {
  const {
    precioCereal,
    cotizacionDolar,
    rindeEsperado,
    precioAgroquimicos,
    precioFertilizantes,
    precioSemillas,
    precioLabores,
    precioSeguros,
    precioAlquiler,
  } = params;

  const pricePerQq =
    precioCereal > 0 && cotizacionDolar > 0 ? precioCereal / cotizacionDolar : 0;

  const costoImplantacionUsd =
    precioAgroquimicos + precioFertilizantes + precioSemillas + precioLabores + precioSeguros;

  // Conversión USD/ha → qq/ha usando precioCereal / cotizacionDolar
  const costoImplantacion = pricePerQq > 0 ? costoImplantacionUsd / pricePerQq : 0;

  // inversionTotal en U$S/ha: suma directa sin conversión
  const inversionTotal = costoImplantacionUsd + precioAlquiler; // U$S/ha
  // conversión a qq/ha solo para ganancia
  const inversionTotalQq = pricePerQq > 0 ? inversionTotal / pricePerQq : 0;

  const ganancia = rindeEsperado - inversionTotalQq;

  const rentabilidad =
    rindeEsperado > 0 && Number.isFinite(ganancia)
      ? (ganancia / rindeEsperado) * 100
      : 0;

  return {
    costoImplantacionUsd,
    costoImplantacion,
    inversionTotal,
    ganancia,
    rentabilidad,
    pricePerQq,
  };
};

interface BuildCultivosProps {
    getValues: UseFormGetValues<FieldValues>
    detail?: GrossMarginRequestDetail | null
}
  /** Convierte los valores del formulario a DTOs listos para el PATCH, recalculando los campos derivados. */
  export const buildCultivosDto = ({ getValues, detail}: BuildCultivosProps): UpdateCultivoDto[] => {
    const formCultivos = getValues("cultivos") as any[];
    return (detail?.cultivos ?? []).map((c, i) => {
      const f = formCultivos[i] ?? {};
      const cotizacionDolar = parseFormValue(f.cotizacionDolar);
      const precioCereal = parseFormValue(f.precioCereal);
      const rindeEsperado = parseFormValue(f.rindeEsperado);
      const precioAgroquimicos = parseFormValue(f.precioAgroquimicos);
      const precioFertilizantes = parseFormValue(f.precioFertilizantes);
      const precioSemillas = parseFormValue(f.precioSemillas);
      const precioLabores = parseFormValue(f.precioLabores);
      const precioSeguros = parseFormValue(f.precioSeguros);
      const precioAlquiler = parseFormValue(f.precioAlquiler);
      const gastoDeCosecha = parseFormValue(f.gastoDeCosecha);

      const calc = calcGrossMarginCultivo({
        precioCereal,
        cotizacionDolar,
        rindeEsperado,
        precioAgroquimicos,
        precioFertilizantes,
        precioSemillas,
        precioLabores,
        precioSeguros,
        precioAlquiler,
        gastoDeCosecha,
      });

      return {
        id: c.id,
        cotizacionDolar,
        precioCereal,
        rindeEsperado,
        precioAgroquimicos,
        precioFertilizantes,
        precioSemillas,
        precioLabores,
        precioSeguros,
        costoImplantacion: calc.costoImplantacion,
        precioAlquiler,
        gastoCosecha: gastoDeCosecha,
        inversionTotal: calc.inversionTotal,
        rentabilidad: calc.rentabilidad,
        ganancia: calc.ganancia,
      };
    });
  };